/* ==========================================================================
   rx-search-redirect.js — sends Rolex searches to the Rolex search page.
   Config: <script type="application/json" data-rx-search-config> written by
   snippets/rx-search-redirect.liquid (rules (a)–(d) are described there).

   1. Every search form / search link on the page (header search, search page
      form, the theme's predictive search "Search for…" button and query
      links): (a) or (b) → the Rolex page; (c) a Rolex reference → asked to the
      predictive search API first; anything else → untouched. The listeners sit
      on window in the capture phase, so they run before the theme's own
      search submit handler (assets/product-search-match.js) and only stop it
      for a Rolex search.
   2. Search results page (config with "results", script not deferred): (a),
      (b) or (d) → location.replace (no extra history entry, so Back works).
      Filtered, sorted or paged result pages are left alone.

   Loaded twice on the search page (results + header); runs once.
   No store values here: everything comes from the config.
   ========================================================================== */
(function () {
  'use strict';

  if (window.rxSearchRedirect) return;
  window.rxSearchRedirect = true;

  var nodes = document.querySelectorAll('script[data-rx-search-config]');
  if (!nodes.length) return;
  var config = null;
  var results = null;
  for (var n = 0; n < nodes.length; n++) {
    try {
      var c = JSON.parse(nodes[n].textContent);
      if (!config) config = c;
      if (c.results) results = c.results;
    } catch (e) {
      // ignore a broken block
    }
  }
  if (!config || !config.url) return;

  function path(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '') || '/';
    } catch (e) {
      return '';
    }
  }
  var SEARCH_PATH = path(config.searchUrl || '/search');
  var TARGET = config.url;
  // Never redirect to the search page itself (would loop).
  if (path(TARGET) === SEARCH_PATH) return;

  // Lowercase, no accents, hyphens / punctuation → spaces, single spaces.
  function norm(value) {
    var s = String(value || '');
    if (s.normalize) s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }
  function prep(list) {
    var out = [];
    (list || []).forEach(function (item) {
      var k = norm(item);
      if (k && out.indexOf(k) === -1) out.push(k);
    });
    return out;
  }
  var KEYWORDS = prep(config.keywords);
  var VENDORS = prep(config.vendors);

  // Whole words: "gmt master ii" matches "Rolex GMT-Master II 40", "gmt" doesn't match "gmtx".
  // A multi-word keyword also matches written as one word ("daydate", "airking").
  function has(q, k) {
    var padded = ' ' + q + ' ';
    if (padded.indexOf(' ' + k + ' ') !== -1) return true;
    return k.indexOf(' ') !== -1 && padded.indexOf(' ' + k.replace(/ /g, '') + ' ') !== -1;
  }

  // Rolex reference shape: optional m, 5–6 digits, optional letters, optional -0000.
  var REF = /^m?(\d{5,6}[a-z]{0,8})(?:-\d{4})?$/;
  function reference(raw) {
    var words = String(raw || '').toLowerCase().trim().split(/\s+/);
    if (words.length !== 1) return '';
    var m = words[0].match(REF);
    return m ? m[1] : '';
  }

  // 'yes' = (a) or (b); 'ref' = (c) shape, needs confirming; '' = not Rolex.
  function classify(raw) {
    var q = norm(raw);
    if (!q) return '';
    if (q.indexOf('rolex') !== -1) return 'yes';
    for (var v = 0; v < VENDORS.length; v++) {
      if (has(q, VENDORS[v])) return '';
    }
    for (var k = 0; k < KEYWORDS.length; k++) {
      if (has(q, KEYWORDS[k])) return 'yes';
    }
    return reference(raw) ? 'ref' : '';
  }

  // (c): every product the predictive search finds for "M<reference>" is Rolex.
  function confirmReference(raw) {
    var ref = reference(raw);
    if (!ref || !config.suggestUrl || !window.fetch) return Promise.resolve(false);
    var url = new URL(config.suggestUrl + '.json', window.location.origin);
    url.searchParams.set('q', 'M' + ref.toUpperCase());
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][fields]', 'title,variants.sku,tag,vendor');
    var timeout = new Promise(function (resolve) { setTimeout(function () { resolve(false); }, 2500); });
    var check = fetch(url.href, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var products = data && data.resources && data.resources.results && data.resources.results.products;
        if (!products || !products.length) return false;
        return products.every(function (p) { return norm(p.vendor) === 'rolex'; });
      })
      .catch(function () { return false; });
    return Promise.race([check, timeout]);
  }

  function go() {
    window.location.assign(TARGET);
  }

  // ---- 1. Search entry points ------------------------------------------------
  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if ((form.getAttribute('method') || 'get').toLowerCase() !== 'get') return false;
    return path(form.getAttribute('action') || window.location.href) === SEARCH_PATH;
  }

  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isSearchForm(form)) return;
    if (form.hasAttribute('data-rx-search-checked')) {
      form.removeAttribute('data-rx-search-checked');
      return;
    }
    var input = form.querySelector('input[name="q"]');
    var query = input ? input.value : '';
    var verdict = classify(query);
    if (!verdict) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (verdict === 'yes') {
      go();
      return;
    }
    confirmReference(query).then(function (ok) {
      if (ok) {
        go();
        return;
      }
      // Not Rolex: submit again through the theme's normal handling.
      form.setAttribute('data-rx-search-checked', '');
      if (form.requestSubmit) form.requestSubmit();
      else form.submit();
    });
  }, true);

  window.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (!link || (link.target && link.target !== '_self')) return;
    var url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (e) {
      return;
    }
    if (url.origin !== window.location.origin || path(url.href) !== SEARCH_PATH) return;
    var query = url.searchParams.get('search_query') || url.searchParams.get('q') || '';
    var verdict = classify(query);
    if (!verdict) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (verdict === 'yes') {
      go();
      return;
    }
    confirmReference(query).then(function (ok) {
      window.location.assign(ok ? TARGET : link.href);
    });
  }, true);

  // ---- 2. Search results page ------------------------------------------------
  // The typed text: search_query, or the words inside product-search-match.js's
  // fielded query (title:"…" OR …) AND (…), or q as it is.
  function typedQuery(params) {
    var supplied = params.get('search_query');
    if (supplied) return supplied;
    var q = params.get('q') || '';
    var words = [];
    var re = /\(title:("(?:[^"\\]|\\.)*")/g;
    var m;
    while ((m = re.exec(q))) {
      try {
        words.push(JSON.parse(m[1]));
      } catch (e) {
        return q;
      }
    }
    return words.length ? words.join(' ') : q;
  }

  if (results && results.performed && path(window.location.href) === SEARCH_PATH) {
    var params = new URLSearchParams(window.location.search);
    var narrowed = false;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0 || key === 'sort_by' || (key === 'page' && value !== '1')) narrowed = true;
    });
    if (!narrowed && (classify(typedQuery(params)) === 'yes' || results.allRolex)) {
      window.location.replace(TARGET);
    }
  }
})();
